import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react'
import { toast } from 'react-toastify'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { useUnsavedInputGuard } from 'components/OrganizationScope/context'
import { PhoneLayout } from 'components/PhoneLayout'
import { pageCardClasses } from 'components/AppLayout/styles'
import { commonStrings, lineStrings } from 'constants/strings'
import {
  useApplyRichMenuToAllChannels,
  useClearDefaultRichMenuFromAllChannels,
  useCurrentRichMenu,
  useLineChannelAccounts,
  useRichMenuImageFileFromCurrentApi,
  useRichMenuTemplates,
  useUploadFile,
} from 'hooks'
import { toErrorMessage } from 'hooks/utils'
import {
  mapLineRichMenuToTemplate,
  normalizeRichMenuImageFileContentType,
  validateRichMenuImageFile,
  type TRichMenuImageValidationResult,
} from 'utils'
import {
  ELineChannelStatus,
  ERichMenuAreaActionType,
  ERichMenuImageFieldLayout,
  type TRichMenuAreaAction,
} from 'types'

import { RichMenuAreaActions } from './RichMenuAreaActions'
import { RichMenuGridPreview } from './RichMenuGridPreview'
import { RichMenuImageField } from './RichMenuImageField'
import { RichMenuTemplateModal } from './RichMenuTemplateModal'
import {
  type TRichMenuAreaFieldErrors,
  validateRichMenuCreateForm,
} from './validateRichMenuCreate'

const richMenuCreate = lineStrings.management.richMenu.create

type TInvalidRichMenuImage = Extract<
  TRichMenuImageValidationResult,
  { ok: false }
>

const toRichMenuImageErrorMessage = (
  validation: TInvalidRichMenuImage,
): string => {
  switch (validation.reason) {
    case 'IMAGE_TOO_LARGE':
      return richMenuCreate.errors.imageTooLarge
    case 'UNSUPPORTED_FORMAT':
      return richMenuCreate.errors.imageTypeInvalid
    case 'UNREADABLE_IMAGE':
      return richMenuCreate.errors.imageUnreadable
    case 'INVALID_DIMENSIONS':
      return richMenuCreate.errors.imageDimensionsInvalid
        .replace('{{width}}', String(validation.width ?? 0))
        .replace('{{height}}', String(validation.height ?? 0))
  }
}

export default function RichMenuPage() {
  const {
    accounts,
    loading: loadingAccounts,
    error: accountsError,
  } = useLineChannelAccounts()
  const {
    templates,
    loading: loadingTemplates,
    error: templatesError,
  } = useRichMenuTemplates()
  const {
    data: currentRichMenuData,
    imageUrl: currentRichMenuImageUrl,
    error: currentRichMenuError,
  } = useCurrentRichMenu()

  const {
    applyToAllChannels,
    isSubmitting,
    error: submitError,
    conflict: applyConflict,
    resetError: resetApplyError,
  } = useApplyRichMenuToAllChannels()
  const {
    clearDefaultFromAllChannels,
    isClearing,
    error: clearDefaultError,
    resetError: resetClearDefaultError,
  } = useClearDefaultRichMenuFromAllChannels()
  const { uploadToS3, loading: uploadingToS3 } = useUploadFile()

  const [templateId, setTemplateId] = useState('')
  const [areas, setAreas] = useState<TRichMenuAreaAction[]>([])
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [showOutlines] = useState(true)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [clearDefaultConfirmOpen, setClearDefaultConfirmOpen] = useState(false)
  const [prefilledFromLine, setPrefilledFromLine] = useState(false)
  const [areaFieldErrors, setAreaFieldErrors] = useState<
    Record<number, TRichMenuAreaFieldErrors>
  >({})
  const [imageError, setImageError] = useState<string | undefined>()
  const [nameError, setNameError] = useState<string | undefined>()
  const [chatBarError, setChatBarError] = useState<string | undefined>()
  const [isProcessingImage, setIsProcessingImage] = useState(false)
  const [expandAreaSignal, setExpandAreaSignal] = useState<{
    key: number
    index: number
  } | null>(null)
  // 初期表示は LINE の現行メニューから複数の effect で組み立てるため、利用者の操作だけを数える
  const [hasUserEdits, setHasUserEdits] = useState(false)
  useUnsavedInputGuard(() => hasUserEdits)
  const actionInFlightRef = useRef(false)
  const imageValidationSequenceRef = useRef(0)
  const imageSelectionAttemptedRef = useRef(false)

  const currentImageHydration =
    useRichMenuImageFileFromCurrentApi(currentRichMenuData)

  useEffect(() => {
    if (imageSelectionAttemptedRef.current) return
    if (imageFile) return

    if (currentImageHydration.file) {
      setImageError(undefined)
      setImageFile(currentImageHydration.file)
      return
    }
  }, [imageFile, currentImageHydration.file])

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId],
  )

  const generatedMenuName = useMemo(() => {
    const fromLabel = selectedTemplate?.label.trim()
    if (fromLabel) return fromLabel
    return richMenuCreate.defaultMenuName
  }, [selectedTemplate])

  useEffect(() => {
    if (templates.length > 0 && !templateId) {
      setTemplateId(templates[0]!.id)
    }
  }, [templates, templateId])

  useEffect(() => {
    if (!selectedTemplate) return
    setAreas((prev) => {
      // Keep existing values when we already have the right shape
      if (prev.length === selectedTemplate.areaKeys.length) {
        return prev
      }

      return selectedTemplate.areaKeys.map(() => ({
        type: ERichMenuAreaActionType.None,
      }))
    })
    setAreaFieldErrors({})
    setExpandAreaSignal(null)
  }, [selectedTemplate])

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(
        imageSelectionAttemptedRef.current ? null : currentRichMenuImageUrl,
      )
      return
    }
    const url = URL.createObjectURL(imageFile)
    setPreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [imageFile, currentRichMenuImageUrl])

  const handleImageFileChange = useCallback(
    async (file: File | null) => {
      if (actionInFlightRef.current) return

      imageSelectionAttemptedRef.current = true
      setHasUserEdits(true)
      const validationSequence = ++imageValidationSequenceRef.current
      setImageError(undefined)

      if (!file) {
        setImageFile(null)
        setIsProcessingImage(false)
        return
      }

      setImageFile(null)
      setIsProcessingImage(true)
      const validation = await validateRichMenuImageFile(file)

      if (validationSequence !== imageValidationSequenceRef.current) return

      setIsProcessingImage(false)
      if (validation.ok === false) {
        const message = toRichMenuImageErrorMessage(validation)
        setImageError(message)
        toast.error(message)
        return
      }

      setImageFile(
        normalizeRichMenuImageFileContentType(file, validation.contentType),
      )
    },
    [],
  )

  const alertMessage =
    accountsError ??
    templatesError ??
    currentRichMenuError ??
    submitError ??
    applyConflict?.message ??
    clearDefaultError
  // 一括適用/解除の対象はbackendが稼働中（ACTIVE/FULL）に限るため、表示条件も揃える
  const hasChannels = accounts.some(
    (account) =>
      account.status === ELineChannelStatus.ACTIVE ||
      account.status === ELineChannelStatus.FULL,
  )
  const isBusy =
    isSubmitting || uploadingToS3 || isProcessingImage || isClearing

  useEffect(() => {
    const richMenu = currentRichMenuData?.richMenu ?? null
    if (!hasChannels) return
    if (!richMenu) return
    if (templates.length === 0) return
    if (prefilledFromLine) return

    const mapped = mapLineRichMenuToTemplate({ templates, richMenu })
    if (!mapped) {
      setPrefilledFromLine(true)
      return
    }

    setTemplateId(mapped.template.id)
    setAreas(mapped.areas)
    setAreaFieldErrors({})
    setExpandAreaSignal(null)
    setPrefilledFromLine(true)
  }, [
    currentRichMenuData,
    hasChannels,
    templates,
    prefilledFromLine,
    setTemplateId,
  ])

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (actionInFlightRef.current || isBusy) {
        return
      }
      if (!selectedTemplate || !hasChannels) return

      setAreaFieldErrors({})
      setImageError(undefined)
      setNameError(undefined)
      setChatBarError(undefined)
      setExpandAreaSignal(null)

      const validation = validateRichMenuCreateForm({
        areas,
        name: generatedMenuName,
        chatBarText: richMenuCreate.chatBarMenu,
        hasImage: !!imageFile,
        imageFileSize: imageFile?.size,
      })

      if (!validation.ok) {
        setAreaFieldErrors(validation.areaErrors)
        if (validation.imageError) setImageError(validation.imageError)
        if (validation.nameError) setNameError(validation.nameError)
        if (validation.chatBarError) setChatBarError(validation.chatBarError)
        if (validation.firstErrorAreaIndex !== undefined) {
          setExpandAreaSignal({
            key: Date.now(),
            index: validation.firstErrorAreaIndex,
          })
        }
        return
      }

      actionInFlightRef.current = true
      setIsProcessingImage(true)
      resetApplyError()
      resetClearDefaultError()

      try {
        const imageValidation = await validateRichMenuImageFile(imageFile!)
        if (imageValidation.ok === false) {
          const message = toRichMenuImageErrorMessage(imageValidation)
          setImageError(message)
          toast.error(message)
          return
        }

        const uploadFile = normalizeRichMenuImageFileContentType(
          imageFile!,
          imageValidation.contentType,
        )
        const [uploaded] = await uploadToS3({ files: [uploadFile] })

        const result = await applyToAllChannels({
          templateId: selectedTemplate.id,
          name: generatedMenuName,
          chatBarText: richMenuCreate.chatBarMenu,
          selected: true,
          areas,
          imageUrl: uploaded.uploadedUrl,
          imageContentType: imageValidation.contentType,
        })
        const deferredIds = new Set(
          result.deferred.map(({ lineChannelId }) => lineChannelId),
        )
        const failedCount = result.failed.filter(
          ({ lineChannelId }) => !deferredIds.has(lineChannelId),
        ).length
        const deferredCount = deferredIds.size

        if (failedCount === 0 && deferredCount === 0) {
          setHasUserEdits(false)
          toast.success(richMenuCreate.submitSuccess)
        } else {
          toast.warning(
            richMenuCreate.submitSuccessPartial
              .replace('{{applied}}', String(result.applied.length))
              .replace('{{failed}}', String(failedCount))
              .replace('{{deferred}}', String(deferredCount)),
          )
        }
      } catch (caught) {
        toast.error(
          toErrorMessage(
            caught instanceof Error ? caught : null,
            richMenuCreate.errors.submitFailed,
          ),
        )
      } finally {
        setIsProcessingImage(false)
        actionInFlightRef.current = false
      }
    },
    [
      isBusy,
      selectedTemplate,
      hasChannels,
      imageFile,
      generatedMenuName,
      areas,
      applyToAllChannels,
      resetApplyError,
      resetClearDefaultError,
      uploadToS3,
    ],
  )

  const handleConfirmClearDefault = useCallback(async () => {
    if (actionInFlightRef.current || isBusy) {
      return
    }

    actionInFlightRef.current = true

    try {
      const result = await clearDefaultFromAllChannels()
      const succeededCount =
        result.cleared.length + result.alreadyCleared.length

      if (succeededCount === 0) {
        toast.error(richMenuCreate.errors.clearDefaultFailed)
        return
      }

      setClearDefaultConfirmOpen(false)

      if (result.failed.length === 0) {
        imageSelectionAttemptedRef.current = true
        setImageFile(null)
        setImageError(undefined)
        toast.success(richMenuCreate.clearDefault.success)
        return
      }

      toast.warning(
        richMenuCreate.clearDefault.successPartial
          .replace('{{cleared}}', String(result.cleared.length))
          .replace('{{alreadyCleared}}', String(result.alreadyCleared.length))
          .replace('{{failed}}', String(result.failed.length)),
      )
    } catch (caught) {
      toast.error(
        toErrorMessage(
          caught instanceof Error ? caught : null,
          richMenuCreate.errors.clearDefaultFailed,
        ),
      )
    } finally {
      actionInFlightRef.current = false
    }
  }, [isBusy, clearDefaultFromAllChannels])

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <div className="mx-auto px-4 pb-4">
          <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-ink">
                {lineStrings.management.richMenu.createTitle}
              </h1>
            </div>
            {hasChannels && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-danger-border text-danger hover:border-danger hover:bg-danger-soft hover:text-danger sm:w-auto"
                disabled={isBusy}
                loading={isClearing}
                onClick={() => {
                  if (actionInFlightRef.current) return
                  resetApplyError()
                  resetClearDefaultError()
                  setClearDefaultConfirmOpen(true)
                }}
              >
                {richMenuCreate.clearDefault.action}
              </Button>
            )}
          </header>

          {alertMessage && (
            <div className="mt-4">
              <Alert>{alertMessage}</Alert>
            </div>
          )}

          {!loadingAccounts && !hasChannels && (
            <div className="mt-4">
              <Alert>{lineStrings.management.richMenu.noChannelsHint}</Alert>
            </div>
          )}

          <form className="mt-6 flex flex-col gap-6" onSubmit={handleSubmit}>
            {hasChannels && (
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-[3fr_7fr] lg:items-start">
                <div className="order-2 flex flex-col items-center gap-3 lg:order-1 lg:sticky lg:top-6">
                  <div
                    className="preview-phone-shadow relative flex min-h-[460px] w-full max-w-[260px] sm:min-h-[540px] sm:max-w-[300px] lg:min-h-[700px] lg:max-w-[375px] flex-col overflow-hidden
                   rounded-[3rem] border-8 border-white shadow-2xl"
                  >
                    <PhoneLayout
                      className="min-h-0 flex-1"
                      title={richMenuCreate.phonePreviewTitle}
                      bodyClassName="flex flex-col p-0 pt-2 pb-1"
                    >
                      <div className="flex min-h-full w-full flex-1 flex-col justify-end">
                        <RichMenuGridPreview
                          template={selectedTemplate}
                          imageUrl={previewUrl}
                          showOutlines={showOutlines}
                        />
                      </div>
                    </PhoneLayout>
                  </div>
                </div>

                <div className="order-1 flex min-w-0 flex-col gap-6 lg:order-2">
                  <div className="rounded-2xl border border-border/60 bg-white p-6">
                    <div className="flex flex-col divide-y divide-border/50">
                      <div className="flex items-center gap-4 pb-4">
                        <div className="min-w-0 text-sm text-ink">
                          <span className="font-medium">
                            {richMenuCreate.templateLabel}
                          </span>
                          <span className="ml-2 text-muted">
                            {selectedTemplate?.label ?? '—'}
                          </span>
                          {nameError ? (
                            <p
                              className="mt-1 text-xs text-red-600"
                              role="alert"
                            >
                              {nameError}
                            </p>
                          ) : null}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          onClick={() => {
                            if (actionInFlightRef.current) return
                            setTemplateModalOpen(true)
                          }}
                          disabled={
                            loadingTemplates || isBusy
                          }
                        >
                          {richMenuCreate.templateChange}
                        </Button>
                      </div>
                      <div className="pt-4">
                        <RichMenuImageField
                          layout={ERichMenuImageFieldLayout.Row}
                          onFileChange={handleImageFileChange}
                          error={imageError}
                          hint={richMenuCreate.imageHint}
                          disabled={isBusy}
                        />
                      </div>
                    </div>
                  </div>

                  {selectedTemplate && (
                    <RichMenuAreaActions
                      areaKeys={selectedTemplate.areaKeys}
                      areas={areas}
                      fieldErrors={areaFieldErrors}
                      expandAreaSignal={expandAreaSignal}
                      disabled={isBusy}
                      onChange={(index, next) => {
                        if (actionInFlightRef.current) return
                        setHasUserEdits(true)
                        setAreaFieldErrors((prev) => {
                          const copy = { ...prev }
                          delete copy[index]
                          return copy
                        })
                        setAreas((prev) => {
                          const copy = [...prev]
                          copy[index] = next
                          return copy
                        })
                      }}
                    />
                  )}

                  {chatBarError ? (
                    <p className="text-right text-xs text-red-600" role="alert">
                      {chatBarError}
                    </p>
                  ) : null}

                  <div className="flex flex-col gap-2 sm:items-end">
                    <Button
                      type="submit"
                      className="w-full sm:w-auto"
                      disabled={
                        isBusy ||
                        !selectedTemplate ||
                        !imageFile ||
                        !hasChannels
                      }
                      loading={
                        isSubmitting || uploadingToS3 || isProcessingImage
                      }
                    >
                      {richMenuCreate.save}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      <RichMenuTemplateModal
        open={templateModalOpen}
        templates={templates}
        selectedId={templateId}
        onClose={() => setTemplateModalOpen(false)}
        onApply={(id) => {
          if (actionInFlightRef.current) return
          setHasUserEdits(true)
          setTemplateId(id)
          setTemplateModalOpen(false)
        }}
      />
      <ConfirmModal
        open={clearDefaultConfirmOpen}
        textTitle={richMenuCreate.clearDefault.confirmTitle}
        textCancel={commonStrings.cancel}
        textConfirm={richMenuCreate.clearDefault.confirm}
        confirmButtonVariant="outline"
        confirmButtonClassName="border-danger bg-danger text-white hover:border-danger hover:bg-danger hover:text-white disabled:shadow-none"
        onClose={() => {
          if (actionInFlightRef.current || isClearing) return
          resetClearDefaultError()
          setClearDefaultConfirmOpen(false)
        }}
        onConfirm={handleConfirmClearDefault}
        isLoading={isClearing}
      />
    </>
  )
}
